"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, Cpu, Layers, Compass, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Foundations() {
  const shouldReduceMotion = useReducedMotion();
  const foundationBlocks = [{
    icon: FileText,
    title: "Commercial landing pages",
    intro: "Product and service page optimisation aligned to procurement-stage questions, ensuring pages actively convert high-intent commercial traffic.",
    bullets: ["Product and service page optimisation structured around buyer evaluation criteria", "Clear, value-focused messaging, tailored on-page copy, and distinct calls to action", "Trust-building elements including detailed process outlines and genuine proof where available", "Rigorous intent alignment so landing pages match the commercial searcher's exact requirement"],
    link: null
  }, {
    icon: Cpu,
    title: "B2B technical SEO",
    intro: "A robust technical infrastructure ensuring your site is discoverable, fast, and structured for complex commercial evaluation.",
    bullets: ["Complete crawlability and indexability across sitemaps, robots.txt, and commercial page pathways", "Logical website architecture reflecting how buyers evaluate products, services, and industries", "Precise canonicalisation, mobile usability, and Core Web Vitals performance optimisation", "Structured data implementation, internal linking architectures, and clean legacy redirect handling", "JavaScript rendering verification to confirm search engines can read dynamic elements accurately"],
    link: {
      text: "technical SEO",
      to: "/TechnicalSeoPage",
      context: "Explore comprehensive ",
      suffix: " services to strengthen site discoverability."
    }
  }, {
    icon: Layers,
    title: "Content for complex buying journeys",
    intro: "B2B buyers evaluate solutions across multiple touchpoints and circulate resources internally before making contact.",
    bullets: ["In-depth service and solution pages covering core challenges, methodology, and next steps", "Dedicated industry and use-case landing pages addressing specific organisational scenarios", "Objective comparison resources and decision-stage FAQs addressing procurement and risk", "Every content piece links directly to relevant commercial landing pages and clear enquiries", "A deliberate strategy focused on commercial relevance rather than high volumes of unfocused posts"],
    link: null
  }, {
    icon: Compass,
    title: "Competitor and commercial search analysis",
    intro: "Strategic evaluation of the commercial search landscape to identify gaps, authority opportunities, and differentiation angles.",
    bullets: ["Identification of non-branded commercial keywords competitors rank for where your site is missing", "Analysis of competitor landing page structures, resource hubs, and core conversion messaging", "Discovery of unanswered buyer questions and high-intent topical content gaps", "Evaluation of competitor internal linking flows and authority patterns without volume promises", "Fresh, tailored analysis for every client to differentiate by industry focus, depth, and support model"],
    link: null
  }];
  return <section id="b2bfoundations" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Core Strategy Pillars
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Landing pages, technical SEO, content and competitors
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            The commercial pages, the technical base, the content and the
            competitive picture all have to work together to turn organic search
            visibility into pipeline contribution.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-8 items-stretch">
          {foundationBlocks.map((block, i) => {
          const Icon = block.icon;
          return <motion.div key={block.title} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                          <Icon className="size-5" />
                        </div>
                        <CardTitle asChild className="text-xl font-semibold text-foreground">
                          <h3 className="text-xl font-semibold text-foreground font-default">
                            {block.title}
                          </h3>
                        </CardTitle>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {block.intro}
                      </p>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <ul className="space-y-3">
                        {block.bullets.map((bullet, bulletIdx) => <li key={bulletIdx} className="flex items-start gap-2.5 text-sm text-foreground/90 leading-relaxed">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                            <span>{bullet}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>
                  {block.link && <div className="px-6 pb-6 pt-2 border-t border-border/40 mt-4">
                      <p className="text-sm text-muted-foreground">
                        {block.link.context}
                        <Link to={block.link.to} className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
                          {block.link.text}
                        </Link>
                        {block.link.suffix}
                      </p>
                    </div>}
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-16 bg-card border border-border rounded-2xl p-8 sm:p-10 text-center max-w-4xl mx-auto shadow-sm">
          <h3 className="text-2xl font-semibold font-default text-foreground">
            Evaluate your current search foundations
          </h3>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            Uncover technical blockers, landing page gaps, and commercial search
            opportunities with an in-depth{" "}
            <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              SEO audit
            </Link>{" "}
            tailored to your sales process.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto rounded-full font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-full font-medium">
              <Link to="/ContactPage?topic=free-seo-audit">Get a Free SEO Audit</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}